import React, { useState, useEffect } from 'react';
import {
  Image as ImageIcon,
  Mic,
  X,
  Download,
  Trash2,
  Play,
  Pause,
  MapPin,
  Calendar,
  User,
  Tag,
  FileText,
  Loader2,
  Grid3x3,
  List,
} from 'lucide-react';
import { storage } from '../utils/firebase/client';
import { ref, listAll, getDownloadURL, deleteObject } from 'firebase/storage';

interface MediaItem {
  id: string;
  companyId: string;
  type: 'photo' | 'voice';
  fileName: string;
  fileSize: number;
  signedUrl: string;
  duration?: number;
  description?: string;
  tags?: string[];
  location?: { latitude: number; longitude: number };
  uploadedAt: string;
  wellId?: string;
  entryId?: string;
  userId: string;
}

interface MediaGalleryProps {
  companyId: string;
  wellId?: string;
  entryId?: string;
  showUpload?: boolean;
  onUpload?: () => void;
}

type ViewMode = 'grid' | 'list';
type FilterType = 'all' | 'photo' | 'voice';

export function MediaGallery({
  companyId,
  wellId,
  entryId,
  showUpload = true,
  onUpload,
}: MediaGalleryProps) {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load media
  useEffect(() => {
    loadMedia();
  }, [companyId, wellId, entryId, filter]);

  const loadMedia = async () => {
    setLoading(true);
    try {
      // Build storage path based on context
      let storagePath: string;
      
      if (wellId) {
        storagePath = `companies/${companyId}/wells/${wellId}/media`;
      } else if (entryId) {
        storagePath = `companies/${companyId}/entries/${entryId}/media`;
      } else {
        storagePath = `companies/${companyId}/media`;
      }

      // List files from Firebase Storage
      const storageRef = ref(storage, storagePath);
      const result = await listAll(storageRef);
      
      // Get download URLs for all items
      const mediaItems: MediaItem[] = await Promise.all(
        result.items.map(async (item) => {
          const url = await getDownloadURL(item);
          const isVoice = item.name.includes('.webm') || item.name.includes('.mp3') || item.name.includes('.wav');
          return {
            id: item.name,
            companyId,
            type: isVoice ? 'voice' : 'photo',
            fileName: item.name,
            fileSize: 0,
            signedUrl: url,
            uploadedAt: new Date().toISOString(),
            wellId,
            entryId,
            userId: '',
          } as MediaItem;
        })
      );

      // Filter by type if needed
      const filteredMedia = filter === 'all' 
        ? mediaItems 
        : mediaItems.filter(m => m.type === filter);
      
      setMedia(filteredMedia);
    } catch (error) {
      console.error('Error loading media:', error);
      setMedia([]); // Return empty on error
    } finally {
      setLoading(false);
    }
  };

  // Delete media
  const deleteMedia = async (mediaId: string) => {
    if (!confirm('Are you sure you want to delete this media?')) return;

    try {
      // Build storage path
      let storagePath: string;
      if (wellId) {
        storagePath = `companies/${companyId}/wells/${wellId}/media/${mediaId}`;
      } else if (entryId) {
        storagePath = `companies/${companyId}/entries/${entryId}/media/${mediaId}`;
      } else {
        storagePath = `companies/${companyId}/media/${mediaId}`;
      }

      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);

      // Remove from list
      setMedia(media.filter(m => m.id !== mediaId));
      setSelectedMedia(null);
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Failed to delete media');
    }
  };

  // Download media
  const downloadMedia = (item: MediaItem) => {
    const link = document.createElement('a');
    link.href = item.signedUrl;
    link.download = item.fileName;
    link.click();
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Filter media by search query
  const filteredMedia = media.filter(item => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      item.fileName.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Media Gallery</h2>
          <p className="text-sm text-gray-600 mt-1">
            {filteredMedia.length} {filter === 'all' ? 'items' : filter === 'photo' ? 'photos' : 'voice notes'}
          </p>
        </div>

        {showUpload && onUpload && (
          <button
            onClick={onUpload}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Upload Media
          </button>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Search */}
        <input
          type="text"
          placeholder="Search media..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />

        {/* Filter */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('photo')}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              filter === 'photo'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <ImageIcon className="w-4 h-4" />
            Photos
          </button>
          <button
            onClick={() => setFilter('voice')}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              filter === 'voice'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Mic className="w-4 h-4" />
            Voice
          </button>
        </div>

        {/* View Mode */}
        <div className="flex items-center gap-2 border border-gray-300 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Grid3x3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Media Grid/List */}
      {filteredMedia.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-gray-400 mb-4">
            {filter === 'photo' ? (
              <ImageIcon className="w-16 h-16 mx-auto" />
            ) : filter === 'voice' ? (
              <Mic className="w-16 h-16 mx-auto" />
            ) : (
              <FileText className="w-16 h-16 mx-auto" />
            )}
          </div>
          <p className="text-gray-600">No media found</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredMedia.map((item) => (
            <div
              key={item.id}
              onClick={() => setSelectedMedia(item)}
              className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
            >
              {item.type === 'photo' ? (
                <img
                  src={item.signedUrl}
                  alt={item.description || item.fileName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                  <Mic className="w-12 h-12 mb-2" />
                  <span className="text-xs">{item.duration ? formatDuration(item.duration) : 'Audio'}</span>
                </div>
              )}

              {/* Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  {item.type === 'photo' ? (
                    <ImageIcon className="w-8 h-8 text-white" />
                  ) : (
                    <Play className="w-8 h-8 text-white" />
                  )}
                </div>
              </div>

              {/* Tags */}
              {item.tags && item.tags.length > 0 && (
                <div className="absolute top-2 left-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
                    <Tag className="w-3 h-3" />
                    {item.tags[0]}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredMedia.map((item) => (
            <div
              key={item.id}
              onClick={() => setSelectedMedia(item)}
              className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
            >
              {/* Thumbnail/Icon */}
              <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                {item.type === 'photo' ? (
                  <img
                    src={item.signedUrl}
                    alt={item.description || item.fileName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                    <Mic className="w-8 h-8" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 truncate">
                  {item.description || item.fileName}
                </h4>
                <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                  <span>{formatFileSize(item.fileSize)}</span>
                  {item.duration && <span>{formatDuration(item.duration)}</span>}
                  <span>{new Date(item.uploadedAt).toLocaleDateString()}</span>
                </div>
                {item.tags && item.tags.length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    {item.tags.map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Type Icon */}
              <div className="flex-shrink-0">
                {item.type === 'photo' ? (
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                ) : (
                  <Mic className="w-6 h-6 text-gray-400" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox/Detail Modal */}
      {selectedMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <div
            className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 truncate">
                {selectedMedia.description || selectedMedia.fileName}
              </h3>
              <button
                onClick={() => setSelectedMedia(null)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {selectedMedia.type === 'photo' ? (
                <img
                  src={selectedMedia.signedUrl}
                  alt={selectedMedia.description || selectedMedia.fileName}
                  className="w-full h-auto max-h-[60vh] object-contain rounded-lg"
                />
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center p-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                    <Mic className="w-24 h-24 text-white" />
                  </div>
                  <audio
                    controls
                    src={selectedMedia.signedUrl}
                    className="w-full"
                    onPlay={() => setPlayingAudio(selectedMedia.id)}
                    onPause={() => setPlayingAudio(null)}
                  />
                </div>
              )}

              {/* Metadata */}
              <div className="mt-6 space-y-4">
                {selectedMedia.description && (
                  <div className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Description</p>
                      <p className="text-sm text-gray-600 mt-1">{selectedMedia.description}</p>
                    </div>
                  </div>
                )}

                {selectedMedia.tags && selectedMedia.tags.length > 0 && (
                  <div className="flex items-start gap-3">
                    <Tag className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Tags</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {selectedMedia.tags.map(tag => (
                          <span key={tag} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {selectedMedia.location && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Location</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedMedia.location.latitude.toFixed(6)}, {selectedMedia.location.longitude.toFixed(6)}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Uploaded</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {new Date(selectedMedia.uploadedAt).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">File Info</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatFileSize(selectedMedia.fileSize)} • {selectedMedia.fileName}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => downloadMedia(selectedMedia)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={() => deleteMedia(selectedMedia.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
